var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
var __spreadArray = this && this.__spreadArray || function (to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
};
import { createSelector } from '@reduxjs/toolkit';
import { chunk } from 'lodash';
import { useMemo } from 'react';
import { sortByLOBName } from '../../common';
import { selectSummaryReport, useSummaryByDisplayMode, useSummaryChunkSize, useSummaryReportAPI } from '../selectors';
var buildLocationTemplate = function (locations) {
  if (locations === void 0) {
    locations = [];
  }
  var byName = new Map();
  locations.forEach(function (location) {
    if (!byName.has(location.locationName)) {
      byName.set(location.locationName, location);
    }
  });
  return Array.from(byName.values());
};
var buildCountryTemplate = function (queues) {
  var byName = new Map();
  queues.forEach(function (queue) {
    var _a;
    (_a = queue.countries) === null || _a === void 0 ? void 0 : _a.forEach(function (country) {
      var _a;
      var existing = byName.get(country.countryName);
      if (!existing) {
        byName.set(country.countryName, __assign(__assign({}, country), {
          locations: buildLocationTemplate(country.locations)
        }));
        return;
      }
      existing.locations = buildLocationTemplate(__spreadArray(__spreadArray([], existing.locations, true), (_a = country.locations) !== null && _a !== void 0 ? _a : [], true));
    });
  });
  return Array.from(byName.values());
};
var buildQueueTemplate = function (queues) {
  var byName = new Map();
  queues.forEach(function (queue) {
    var existing = byName.get(queue.name);
    if (!existing) {
      byName.set(queue.name, __assign(__assign({}, queue), {
        countries: buildCountryTemplate([queue])
      }));
      return;
    }
    existing.countries = buildCountryTemplate([existing, queue]);
  });
  return Array.from(byName.values());
};
var buildLobTemplate = function (lobName, perScenarioLob) {
  return {
    lobName: lobName,
    defQueues: buildQueueTemplate(perScenarioLob.flatMap(function (lob) {
      var _a;
      return (_a = lob.defQueues) !== null && _a !== void 0 ? _a : [];
    })),
    nonDefQueues: buildQueueTemplate(perScenarioLob.flatMap(function (lob) {
      var _a;
      return (_a = lob.nonDefQueues) !== null && _a !== void 0 ? _a : [];
    }))
  };
};
var selectSupportStaffsPerScenario = function (chunkSize) {
  return createSelector(selectSummaryReport, function (summaryReports) {
    if (!(summaryReports === null || summaryReports === void 0 ? void 0 : summaryReports.length)) return [];
    // Collect all role names from all scenarios to get a unique complete list
    var roles = Array.from(new Set(summaryReports.flatMap(function (s) {
      var _a, _b;
      return (_b = (_a = s.supportStaffs) === null || _a === void 0 ? void 0 : _a.map(function (ss) {
        return ss.roleName;
      })) !== null && _b !== void 0 ? _b : [];
    })));
    return roles.map(function (roleName) {
      var perScenario = summaryReports.map(function (s) {
        var _a, _b, _c, _d;
        var entry = (_a = s.supportStaffs) === null || _a === void 0 ? void 0 : _a.find(function (ss) {
          return ss.roleName === roleName;
        });
        return {
          value: (_b = entry === null || entry === void 0 ? void 0 : entry.value) !== null && _b !== void 0 ? _b : 0,
          supportStaffSalaries: (_c = entry === null || entry === void 0 ? void 0 : entry.supportStaffSalaries) !== null && _c !== void 0 ? _c : 0,
          queuesByLob: (_d = entry === null || entry === void 0 ? void 0 : entry.queuesByLob) !== null && _d !== void 0 ? _d : []
        };
      });
      var lobNames = Array.from(new Set(perScenario.flatMap(function (s) {
        return s.queuesByLob.map(function (lob) {
          return lob.lobName;
        });
      }))).sort(function (a, b) {
        return sortByLOBName({
          name: a,
          lobName: a
        }, {
          name: b,
          lobName: b
        });
      });
      var mergedQueuesByLob = lobNames.map(function (lobName) {
        var perScenarioLob = perScenario.map(function (s) {
          var entry = s.queuesByLob.find(function (lob) {
            return lob.lobName === lobName;
          });
          return entry !== null && entry !== void 0 ? entry : {
            lobName: lobName,
            defQueues: [],
            nonDefQueues: []
          };
        });
        return buildLobTemplate(lobName, perScenarioLob);
      });
      return {
        roleName: roleName,
        // one value per scenario (chunked for multi-year support)
        values: chunk(perScenario.map(function (s) {
          return s.value;
        }), chunkSize),
        // one salary value per scenario (chunked for multi-year support)
        salaryValues: chunk(perScenario.map(function (s) {
          return s.supportStaffSalaries;
        }), chunkSize),
        // all scenarios share the same merged template structure
        queuesByLob: mergedQueuesByLob,
        // per-scenario per-queue geo breakdown, chunked: [year][scenario][lobIdx][queueIdx]
        perScenarioQueues: chunk(perScenario.map(function (s) {
          return s.queuesByLob;
        }), chunkSize)
      };
    });
  });
};
export var useSelectSupportStaffsPerScenario = function () {
  var data = useSummaryByDisplayMode();
  var chunkSize = useSummaryChunkSize();
  var selector = useMemo(function () {
    return selectSupportStaffsPerScenario(chunkSize);
  }, [chunkSize]);
  return selector(data);
};
export var useHasGeoSupportStaff = function () {
  var data = useSummaryReportAPI()[0].data;
  return useMemo(function () {
    return !!(data === null || data === void 0 ? void 0 : data.some(function (scenario) {
      var _a;
      return (_a = scenario.supportStaffs) === null || _a === void 0 ? void 0 : _a.some(function (role) {
        var _a;
        return (_a = role.queuesByLob) === null || _a === void 0 ? void 0 : _a.some(function (lob) {
          return __spreadArray(__spreadArray([], lob.defQueues, true), lob.nonDefQueues, true).some(function (queue) {
            return queue.countries.some(function (country) {
              return country.locations.length;
            });
          });
        });
      });
    }));
  }, [data]);
};